import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
import { FaqAccordion } from '../components/FaqAccordion';
export function Book() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const faqItems = [{
    question: "What this is — and what it isn't",
    answer: 'Growth That Holds exists to help founder-led companies identify and remove the structural constraint making growth heavier, slower, and more dependent than it should be. This is not framework installation, fractional operational ownership, or generic advisory. It is targeted business architecture work — find the right constraint, redesign the right part of the system, and leave the business more independent than it was before.'
  }, {
    question: 'Is this like EOS, Scaling Up, or another business framework?',
    answer: 'No. Frameworks can be useful. But this work starts earlier. It helps determine what is actually broken, what is being misread, and what needs to change before any framework, operator, or tool is likely to help. If the company already knows the problem and mainly needs framework adoption, this is probably not the right fit. If growth is working but the business feels heavier, slower, and harder to trust — and nobody can cleanly explain why — this is where the work fits.'
  }, {
    question: 'Is this fractional COO work?',
    answer: 'No. A fractional COO usually helps run the business over time. This work is narrower and more targeted. The goal is to identify and redesign the structural issue behind the recurring friction, then leave behind something the business can run without ongoing dependence on me.'
  }, {
    question: 'Is this consulting?',
    answer: 'Yes, but not in the loose sense people usually mean. This is not broad strategy advice, open-ended recommendations, or a long list of observations. It is structured diagnosis and focused redesign around the highest-leverage constraint in the business.'
  }, {
    question: 'Do you take over execution?',
    answer: 'No. I do not become the long-term escalation path, own day-to-day throughput, or stay in the middle to keep things moving. The work is designed to reduce dependence, not shift it from the founder to an outside operator.'
  }, {
    question: 'What kinds of problems is this actually for?',
    answer: 'Usually some version of this: growth is happening, but the business feels heavier than it should. Important decisions still route through a few people. Margins are thinning in places nobody can fully explain. Delivery absorbs too many exceptions. Different teams are working from different definitions of reality. That is usually a structural problem, not a motivation problem.'
  }, {
    question: 'What do you actually change?',
    answer: 'That depends on the constraint. Sometimes it is decision thresholds and escalation rules. Sometimes it is offer boundaries or promise-to-delivery misalignment. Sometimes it is ownership, interfaces, or the signals the business is using to manage itself. The point is not to redesign everything. The point is to fix the one thing making the rest of the system harder to run.'
  }, {
    question: 'Is this about AI readiness?',
    answer: "Sometimes, yes. But AI is usually not the first problem. If ownership is fuzzy, definitions are inconsistent, exceptions are unmanaged, or decisions still rely on founder judgment that lives in people's heads — AI tends to amplify confusion instead of reducing it. Clarity comes first. Then automation."
  }, {
    question: 'How is this different from hiring a strong operator?',
    answer: 'A strong operator can be incredibly valuable. But bringing in an operator before the underlying structural issue is clear can create a different version of the same problem — now the business depends on a talented person to absorb ambiguity instead of fixing the ambiguity itself. This work helps make the business easier to run well, by anyone.'
  }, {
    question: 'When is this the right fit?',
    answer: 'When growth is working but getting harder to sustain cleanly. When the business feels more dependent on key people than it should. When the same issues keep coming back in different forms. When you do not want more activity — you want the right structural fix.'
  }, {
    question: 'What is the outcome?',
    answer: 'The outcome is not a pile of recommendations. It is a business that can make better decisions, absorb less avoidable friction, and run more cleanly without constant founder backstopping. That usually looks like clearer authority, fewer recurring exceptions, cleaner handoffs, better economic visibility, less drag, and more independence.'
  }];
  return <main className="w-full" data-id="element-317">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-paper pt-32 overflow-hidden" data-id="element-318">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-319">
          <BlueprintGrid data-id="element-320" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center relative z-10" data-id="element-321">
          <motion.p variants={fadeIn} className="eyebrow mb-6 text-seaglass" data-id="element-322">
            Next Step
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-323">
            Book an Honest Conversation
          </motion.h1>

          <motion.p variants={fadeIn} className="text-lg text-text max-w-2xl mx-auto mb-8" data-id="element-324">
            A 45-minute working conversation to pressure-test what you are
            seeing, separate deeper drag from normal growth friction, and decide
            the next right step.
          </motion.p>

          {/* Hero CTA */}
          <motion.div variants={fadeIn} className="flex flex-col items-center gap-2 mb-10" data-id="element-325">
            <a href="#calendar" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-326">
              <span data-id="element-327">Choose a time</span>
              <ChevronDown className="w-5 h-5" data-id="element-328" />
            </a>
            <p className="text-sm text-text-muted" data-id="element-329">
              Video call. You do not need to prep.
            </p>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center" animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-330">
          <ChevronDown className="w-5 h-5 text-seaglass/50" data-id="element-331" />
        </motion.div>
      </section>

      {/* What to expect */}
      <section className="py-24 px-6 bg-paper-dark border-t hairline-top" data-id="element-332">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-333">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-334">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-335">
              What to expect
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-336">
            <p className="text-body text-text" data-id="element-337">This is not a pitch call.</p>
            <p className="text-body text-text" data-id="element-338">We will use the time to:</p>
            <ul className="space-y-3 pl-1" data-id="element-339">
              {['pressure-test what you are seeing', 'clarify where the drag may actually be coming from', 'decide whether this looks structural or not', 'make the next step clearer'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-340">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-341" />
                  <span className="text-body text-text" data-id="element-342">{item}</span>
                </li>)}
            </ul>

            <div className="mt-8 space-y-4" data-id="element-343">
              <p className="text-body text-text" data-id="element-344">
                You do not need a polished diagnosis.
              </p>
              <p className="text-body text-text" data-id="element-345">
                You do not need a slide deck.
              </p>
              <p className="text-body text-text" data-id="element-346">
                You do not need to know the right language for the problem.
              </p>
              <p className="text-body text-navy font-medium" data-id="element-347">
                You just need to be honest about what is getting heavier.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* What You'll Leave With */}
      <section className="py-24 px-6 bg-paper" data-id="element-348">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-349">
          <motion.div variants={fadeIn} className="mb-16 text-center" data-id="element-350">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-351">
              What you&apos;ll leave with
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-352">
            <motion.div variants={fadeIn} className="p-8 bg-white border border-seaglass/10" data-id="element-353">
              <div className="flex items-center space-x-3 mb-4" data-id="element-354">
                <Check className="w-6 h-6 text-seaglass flex-shrink-0" data-id="element-355" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3 leading-tight" data-id="element-356">
                Clearer picture
              </h3>
              <p className="text-sm text-text leading-relaxed" data-id="element-357">
                A better read on whether the issue is structural or just
                temporary strain, and what "better" should actually look like
                from here.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="p-8 bg-white border border-seaglass/10" data-id="element-358">
              <div className="flex items-center space-x-3 mb-4" data-id="element-359">
                <Check className="w-6 h-6 text-seaglass flex-shrink-0" data-id="element-360" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3 leading-tight" data-id="element-361">
                Honest assessment
              </h3>
              <p className="text-sm text-text leading-relaxed" data-id="element-362">
                A direct view of what may actually be causing the drag,
                including "keep pushing" if that is the truth.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="p-8 bg-white border border-seaglass/10" data-id="element-363">
              <div className="flex items-center space-x-3 mb-4" data-id="element-364">
                <Check className="w-6 h-6 text-seaglass flex-shrink-0" data-id="element-365" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3 leading-tight" data-id="element-366">
                Clean next step
              </h3>
              <p className="text-sm text-text leading-relaxed" data-id="element-367">
                A real next move: keep pushing, wait, or move into a Constraint
                Diagnostic.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Possible Outcomes */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-368">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-369">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-370">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-371">
              Possible outcomes
            </h2>
            <p className="text-lg text-text" data-id="element-372">
              There are only a few honest answers that come out of this call:
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-373">
            <ul className="space-y-4" data-id="element-374">
              <li className="flex items-start space-x-3" data-id="element-375">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-376" />
                <span className="text-body text-text" data-id="element-377">
                  <strong className="font-semibold text-navy" data-id="element-378">
                    This is normal growth friction.
                  </strong>{' '}
                  Keep pushing.
                </span>
              </li>
              <li className="flex items-start space-x-3" data-id="element-379">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-380" />
                <span className="text-body text-text" data-id="element-381">
                  <strong className="font-semibold text-navy" data-id="element-382">
                    This looks real, but now is not the right time to touch it.
                  </strong>{' '}
                  Wait.
                </span>
              </li>
              <li className="flex items-start space-x-3" data-id="element-383">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-384" />
                <span className="text-body text-text" data-id="element-385">
                  <strong className="font-semibold text-navy" data-id="element-386">
                    This is a structural issue worth diagnosing.
                  </strong>{' '}
                  Move into a Constraint Diagnostic.
                </span>
              </li>
            </ul>

            <div className="mt-8" data-id="element-387">
              <p className="text-body text-navy font-medium" data-id="element-388">
                The point of the conversation is clarity, not pressure.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Reassurance band */}
      <section className="relative py-20 px-6 bg-navy grain overflow-hidden" data-id="element-389">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-390">
          <BlueprintGrid data-id="element-391" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-392">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-white mb-4 leading-tight" data-id="element-393">
            No pitch. No obligation. Just clarity.
          </h2>
          <p className="text-lg text-white/80 mb-4" data-id="element-394">
            If there is a real business problem worth fixing, I will help make
            it visible. If there is not, I will tell you that too. That is the
            whole point of the conversation.
          </p>
          <p className="text-lg text-white/70 mb-8" data-id="element-395">
            If the solution depends on me staying involved, we did not solve the
            right problem.
          </p>
          <a href="#calendar" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-396">
            <span data-id="element-397">Choose a time</span>
            <ChevronDown className="w-5 h-5" data-id="element-398" />
          </a>
        </motion.div>
      </section>

      {/* Calendly Embed */}
      <section id="calendar" className="py-24 px-6 bg-paper-dark grain" data-id="element-399">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-4xl mx-auto" data-id="element-400">
          <div className="p-8 bg-paper/30 border border-seaglass/10" data-id="element-401">
            <iframe className="bg-white w-full" src="https://calendly.com/johnwestley/honest-conversation?hide_gdpr_banner=1" title="Book an Honest Conversation" style={{
            minWidth: '320px',
            height: '700px'
          }} frameBorder="0" data-id="element-402"></iframe>
          </div>

          {/* Fallback link */}
          <p className="text-center text-sm text-text-muted mt-4" data-id="element-403">
            If the calendar doesn&apos;t load,{' '}
            <a href="https://calendly.com/johnwestley/honest-conversation" target="_blank" rel="noopener noreferrer" className="text-seaglass hover:text-seaglass-light underline transition-colors duration-200" data-id="element-404">
              open it here
            </a>
            .
          </p>

          <div className="mt-12 text-center space-y-4" data-id="element-405">
            <p className="text-text" data-id="element-406">Questions before booking?</p>
            <a href="mailto:john@growththatholds.com" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-407">
              <Mail className="w-4 h-4" data-id="element-408" />
              <span data-id="element-409">john@growththatholds.com</span>
            </a>
            <p className="text-sm text-text-muted" data-id="element-410">
              If we&apos;re not a fit, I will tell you quickly.
            </p>
            <div className="pt-4" data-id="element-411">
              <a href="#calendar" className="inline-flex items-center space-x-2 text-sm text-text-muted hover:text-navy transition-colors duration-300" data-id="element-412">
                <ChevronUp className="w-4 h-4" data-id="element-413" />
                <span data-id="element-414">Back to calendar</span>
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-paper" data-id="element-415">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-416">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-417">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-418">
              Common questions
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-419">
            <FaqAccordion items={faqItems} data-id="element-420" />
          </motion.div>
        </motion.div>
      </section>
    </main>;
}